import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Download, ScrollText } from 'lucide-react';
import { AuditLog, AuditLogQuery } from '../../types/audit';
import { auditService } from '../../services/auditService';
import { asNumber, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import { Button, Card, EmptyState, IconButton, Menu, Skeleton, Spinner } from '../ui';
import { ActionPill, AuditDetails, EntityTypeLabel, formatShortDateTime, shortId } from './auditFormat';

interface AuditLogDisplayProps {
  initialQuery?: AuditLogQuery;
  showFilters?: boolean;
  showExport?: boolean;
  className?: string;
}

const hasDetails = (log: AuditLog) =>
  (log.details !== null && log.details !== undefined && log.details !== '') || !!log.ipAddress || !!log.entityId;

/* One codec per parameter, made once: the URL state reads them on every render */
const PAGE = asNumber(1);

export const AuditLogDisplay: React.FC<AuditLogDisplayProps> = ({
  initialQuery = {},
  showFilters = true,
  showExport = true,
  className = '',
}) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0,
  });
  const [exporting, setExporting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // The filters come from the caller; the page and the open row are in the
  // address bar, so a link lands on the same page with the same row unfolded
  const initialKey = JSON.stringify(initialQuery);
  const base = useMemo(() => JSON.parse(initialKey) as AuditLogQuery, [initialKey]);
  const [pageParam] = useUrlState<number | undefined>('page', base.page, PAGE);
  const [expanded, setExpanded] = useUrlState('row', '');
  const write = useUrlWriter();

  const query = useMemo<AuditLogQuery>(() => ({ ...base, page: pageParam }), [base, pageParam]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await auditService.getLogs(query);
      setLogs(response.logs);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [query]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handlePageChange = (next: number) => {
    // A row left open on the page before is not on this one
    write({ page: next > 1 ? String(next) : null, row: null });
  };

  const handleExport = async (exportFormat: 'csv' | 'excel') => {
    setExporting(true);
    try {
      const blob = await auditService.exportLogs(query, exportFormat);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileExtension = exportFormat === 'excel' ? 'xlsx' : 'csv';
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export audit logs');
      console.error('Error exporting audit logs:', err);
    } finally {
      setExporting(false);
    }
  };

  const toggle = (log: AuditLog) => {
    if (!hasDetails(log)) return;
    setExpanded(expanded === log.id ? '' : log.id);
  };

  const initialLoad = loading && !loaded;
  const { page, totalPages, total, limit } = pagination;

  const detailsPanel = (log: AuditLog) => (
    <div className="space-y-2">
      <AuditDetails details={log.details} />
      <p className="flex flex-wrap gap-x-3 gap-y-0.5 font-ui-mono text-[11.5px] text-neutral-400">
        {log.entityId && <span className="break-all">{log.entityId}</span>}
        {log.ipAddress && <span>{log.ipAddress}</span>}
        <span>{new Date(log.createdAt).toLocaleString()}</span>
      </p>
    </div>
  );

  return (
    <Card clip className={className}>
      {/* Header row */}
      <div className="flex h-12 items-center justify-between gap-3 px-4">
        <p className="text-[13px] tabular-nums text-neutral-500">
          {initialLoad ? ' ' : `${total.toLocaleString()} ${total === 1 ? 'event' : 'events'}`}
          {loading && !initialLoad && <Spinner className="ml-2 h-3 w-3 align-[-1px] text-neutral-400" />}
        </p>
        {showExport && (
          <Menu
            items={[
              { label: 'CSV', onSelect: () => handleExport('csv') },
              { label: 'Excel', onSelect: () => handleExport('excel') },
            ]}
            trigger={props => (
              <Button size="sm" variant="secondary" loading={exporting} {...props}>
                {!exporting && <Download className="h-4 w-4" strokeWidth={1.75} />}
                Export
              </Button>
            )}
          />
        )}
      </div>

      {error && (
        <p role="alert" className="mx-4 mb-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      {initialLoad ? (
        <div data-testid="loading-spinner" className="divide-y divide-neutral-100 border-t border-neutral-200/70">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-4 w-16 rounded-full" />
              <Skeleton className="hidden h-3.5 w-32 sm:block" />
            </div>
          ))}
        </div>
      ) : logs.length === 0 ? (
        !error && <EmptyState icon={ScrollText} title="No events" className="border-t border-neutral-200/70" />
      ) : (
        <div className={`transition-opacity ${loading ? 'opacity-60' : ''}`}>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full table-fixed border-collapse text-left text-[13px]">
              <thead className="border-y border-neutral-200/70">
                <tr>
                  <th className="h-9 w-[150px] whitespace-nowrap px-4 text-[12px] font-normal text-neutral-400">Time</th>
                  <th className="h-9 whitespace-nowrap px-4 text-[12px] font-normal text-neutral-400">Actor</th>
                  <th className="h-9 w-[120px] whitespace-nowrap px-4 text-[12px] font-normal text-neutral-400">Action</th>
                  <th className="h-9 w-[34%] whitespace-nowrap px-4 text-[12px] font-normal text-neutral-400">Entity</th>
                  <th className="w-11 px-2">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const open = expanded === log.id;
                  const expandable = hasDetails(log);
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        onClick={() => toggle(log)}
                        className={`border-t border-neutral-100 transition-colors first:border-t-0 ${
                          expandable ? 'cursor-pointer hover:bg-neutral-50' : ''
                        } ${open ? 'bg-neutral-50' : ''}`}
                      >
                        <td
                          className="h-11 whitespace-nowrap px-4 tabular-nums text-neutral-500"
                          title={new Date(log.createdAt).toLocaleString()}
                        >
                          {formatShortDateTime(log.createdAt)}
                        </td>
                        <td className="h-11 px-4">
                          <span className="block truncate text-ink" title={`${log.user?.email} · ${log.user?.role}`}>
                            {log.user?.email ?? 'System'}
                          </span>
                        </td>
                        <td className="h-11 px-4">
                          <ActionPill action={log.action} />
                        </td>
                        <td className="h-11 truncate px-4">
                          {log.entityType ? (
                            <span className="inline-flex max-w-full items-center gap-2">
                              <EntityTypeLabel type={log.entityType} className="text-ink" />
                              {log.entityId && (
                                <span className="font-ui-mono text-[11.5px] text-neutral-400">{shortId(log.entityId)}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          )}
                        </td>
                        <td className="h-11 px-2 text-right">
                          {expandable && (
                            <button
                              type="button"
                              aria-expanded={open}
                              aria-label={open ? 'Hide details' : 'Show details'}
                              onClick={e => {
                                e.stopPropagation();
                                toggle(log);
                              }}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                            >
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                                strokeWidth={1.75}
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-neutral-50">
                          <td />
                          <td colSpan={4} className="px-4 pb-3 pt-0.5">
                            {detailsPanel(log)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Phone rows */}
          <ul className="divide-y divide-neutral-200/70 border-t border-neutral-200/70 sm:hidden">
            {logs.map(log => {
              const open = expanded === log.id;
              return (
                <li key={log.id} className={open ? 'bg-neutral-50' : ''}>
                  <button
                    type="button"
                    onClick={() => toggle(log)}
                    aria-expanded={hasDetails(log) ? open : undefined}
                    className="flex min-h-[56px] w-full items-center gap-3 px-4 py-2.5 text-left active:bg-neutral-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[14px] text-ink">{log.user?.email ?? 'System'}</span>
                      </span>
                      <span className="mt-1 flex items-center gap-2 text-[12px] text-neutral-400">
                        <ActionPill action={log.action} />
                        {log.entityType && <EntityTypeLabel type={log.entityType} />}
                      </span>
                    </span>
                    <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
                      {formatShortDateTime(log.createdAt)}
                    </span>
                  </button>
                  {open && <div className="px-4 pb-3">{detailsPanel(log)}</div>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 border-t border-neutral-200/70 px-4 py-2">
          <span className="text-[12px] tabular-nums text-neutral-400">
            {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <span className="mr-1 text-[12px] tabular-nums text-neutral-400">
              {page} / {totalPages}
            </span>
            <IconButton label="Previous page" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || loading}>
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
            <IconButton
              label="Next page"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </div>
        </div>
      )}
    </Card>
  );
};
